import { PeriodeData, PeriodeQuery, ExportQuery, Periode } from "../utils/types";

// Simulation des données - À remplacer par des appels Axios réels
class RevenusService {
  private simulateDelay(ms: number = 500): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private generateData(periode: Periode): PeriodeData {
    // Données de base adaptées à la période
    const multiplicateurs = {
      journalier: 1,
      hebdomadaire: 7,
      mensuel: 30,
      trimestriel: 90,
      semestriel: 180,
      annuel: 365,
    };

    const multiplicateur = multiplicateurs[periode];

    return {
      periode,
      dateDebut: "2024-01-01",
      dateFin: "2024-01-31",
      metriquesPrincipales: {
        revenuTotal: (37500 * multiplicateur) / 30,
        beneficeNetTotal: (28750 * multiplicateur) / 30,
        evolutionPourcentage: 8.5,
        margeMoyenne: 2.8,
      },
      beneficesNets: {
        total: (28750 * multiplicateur) / 30,
        evolutionMoisPrecedent: 8.5,
        breakdown: {
          commissionsTotales: (12500 * multiplicateur) / 30,
          margeChange: (4200 * multiplicateur) / 30,
          coutsPayouts: (5800 * multiplicateur) / 30,
          coutsFixes: (3150 * multiplicateur) / 30,
          autresRevenus: (1200 * multiplicateur) / 30,
          fraisBancaires: (800 * multiplicateur) / 30,
        },
      },
      margeChange: {
        margeMoyenne: 2.8,
        profitTotal: (4200 * multiplicateur) / 30,
        evolution: this.generateMargeEvolution(periode),
        tauxReference: {
          tauxReel: 655,
          tauxApplique: 672,
          deviseBase: "CAD",
          deviseCible: "FCFA",
        },
      },
      coutsPartenaires: this.generatePartenaires(periode),
      evolutionBenefice: this.generateEvolutionBenefice(periode),
      profitsParService: this.generateServices(periode),
      resumeGlobal: {
        revenuMoyenParTransaction: 32.5,
        coutMoyenParTransaction: 7.8,
        beneficeMoyenParTransaction: 24.7,
        tauxConversion: 68.5,
        customerLifetimeValue: 450,
        churnRate: 12.3,
      },
    };
  }

  private generateMargeEvolution(periode: Periode) {
    const periodes = {
      journalier: ["08h", "10h", "12h", "14h", "16h", "18h"],
      hebdomadaire: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
      mensuel: ["S1", "S2", "S3", "S4"],
      trimestriel: ["M1", "M2", "M3"],
      semestriel: ["T1", "T2"],
      annuel: ["Q1", "Q2", "Q3", "Q4"],
    };

    return periodes[periode].map((p, i) => ({
      periode: p,
      marge: 2.5 + i * 0.2,
      volume: 150000 + i * 10000,
    }));
  }

  private generatePartenaires(periode: Periode) {
    const multiplicateurs = {
      journalier: 1 / 30,
      hebdomadaire: 7 / 30,
      mensuel: 1,
      trimestriel: 3,
      semestriel: 6,
      annuel: 12,
    };

    const multiplicateur = multiplicateurs[periode];

    return [
      {
        partenaireId: "orange_money",
        nom: "Orange Money",
        type: "mobile_money" as const,
        volumeTraite: 15000 * multiplicateur,
        fraisTotaux: 450 * multiplicateur,
        roi: 6.5,
        nombreTransactions: 150 * multiplicateur,
        coutMoyenParTransaction: 3.0,
      },
      {
        partenaireId: "mtn_money",
        nom: "MTN Money",
        type: "mobile_money" as const,
        volumeTraite: 8000 * multiplicateur,
        fraisTotaux: 320 * multiplicateur,
        roi: 5.8,
        nombreTransactions: 80 * multiplicateur,
        coutMoyenParTransaction: 4.0,
      },
      {
        partenaireId: "moneris",
        nom: "Moneris",
        type: "payment_processor" as const,
        volumeTraite: 100000 * multiplicateur,
        fraisTotaux: 200 * multiplicateur,
        roi: 3.2,
        nombreTransactions: 500 * multiplicateur,
        coutMoyenParTransaction: 0.4,
      },
      {
        partenaireId: "interac",
        nom: "INTERAC",
        type: "bank_transfer" as const,
        volumeTraite: 2000000 * multiplicateur,
        fraisTotaux: 125 * multiplicateur,
        roi: 1.5,
        nombreTransactions: 1000 * multiplicateur,
        coutMoyenParTransaction: 0.125,
      },
    ];
  }

  private generateEvolutionBenefice(periode: Periode) {
    const periodes = {
      journalier: ["08h", "10h", "12h", "14h", "16h", "18h"],
      hebdomadaire: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
      mensuel: ["Oct", "Nov", "Dec"],
      trimestriel: ["Q1", "Q2", "Q3"],
      semestriel: ["S1", "S2"],
      annuel: ["2021", "2022", "2023"],
    };

    return periodes[periode].map((p, i) => ({
      periode: p,
      revenus: 34500 + i * 1500,
      beneficeNet: 26500 + i * 1150,
      coutsTotaux: 8000 + i * 350,
      margeNet: 76.8,
    }));
  }

  private generateServices(periode: Periode) {
    const multiplicateurs = {
      journalier: 1 / 30,
      hebdomadaire: 7 / 30,
      mensuel: 1,
      trimestriel: 3,
      semestriel: 6,
      annuel: 12,
    };

    const multiplicateur = multiplicateurs[periode];

    return [
      {
        serviceId: "transferts_standard",
        nom: "Transferts Standard",
        categorie: "transferts" as const,
        revenus: 28500 * multiplicateur,
        couts: 25800 * multiplicateur,
        beneficeNet: 2700 * multiplicateur,
        marge: 9.5,
        volumeTransactions: 950 * multiplicateur,
        revenuMoyenParTransaction: 30.0,
      },
      {
        serviceId: "transferts_express",
        nom: "Transferts Express",
        categorie: "transferts" as const,
        revenus: 12800 * multiplicateur,
        couts: 11900 * multiplicateur,
        beneficeNet: 900 * multiplicateur,
        marge: 7.0,
        volumeTransactions: 320 * multiplicateur,
        revenuMoyenParTransaction: 40.0,
      },
      {
        serviceId: "change_devises",
        nom: "Change de devises",
        categorie: "forex" as const,
        revenus: 1500 * multiplicateur,
        couts: 1100 * multiplicateur,
        beneficeNet: 400 * multiplicateur,
        marge: 26.6,
        volumeTransactions: 75 * multiplicateur,
        revenuMoyenParTransaction: 20.0,
      },
    ];
  }

  async getRevenusData(query: PeriodeQuery): Promise<PeriodeData> {
    await this.simulateDelay(800); // Simulation délai réseau
    return this.generateData(query.periode);
  }

  async exportRevenus(query: ExportQuery): Promise<void> {
    await this.simulateDelay(1000); // Simulation délai export

    // Simulation de téléchargement
    const data = this.generateData(query.periode);
    const rapport = JSON.stringify(data, null, 2);
    const blob = new Blob([rapport], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport-revenus-${query.periode}-${new Date().toISOString().split("T")[0]}.${query.format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const revenusService = new RevenusService();
